import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Upload, Trash2, FileSpreadsheet, Database, ChevronDown, ChevronUp, Loader2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Processing() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const fileInputRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchReports = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/processing`);
      setReports(r.data || []);
      // Expand most recent by default on first load
      if (r.data && r.data.length > 0) {
        setExpanded((prev) => (Object.keys(prev).length === 0 ? { [r.data[0].id]: true } : prev));
      }
    } catch (err) {
      toast.error('فشل تحميل التقارير');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, []);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(xls|xlsx)$/i.test(file.name)) {
      toast.error('يرجى اختيار ملف Excel (.xls أو .xlsx)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/processing/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم رفع التقرير بنجاح');
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل رفع الملف');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التقرير؟')) return;
    try {
      await axios.delete(`${API_URL}/api/processing/${id}`);
      toast.success('تم حذف التقرير');
      fetchReports();
    } catch {
      toast.error('فشل حذف التقرير');
    }
  };

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-6" data-testid="processing-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-primary flex items-center gap-3">
            <Database className="h-7 w-7" />
            قسم المعالجة
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">رفع تقارير المعالجة من ملف Excel وعرض إجراءات الموظفين</p>
        </div>

        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx"
            onChange={handleUpload}
            className="hidden"
            data-testid="processing-file-input"
          />
          {isAdmin && (
            <Button
              onClick={handlePickFile}
              disabled={uploading}
              data-testid="upload-processing-button"
              className="gap-2"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? 'جاري الرفع...' : 'رفع ملف Excel'}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <Card className="p-12 text-center text-muted-foreground">جاري التحميل...</Card>
      ) : reports.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-2" data-testid="processing-empty">
          <FileSpreadsheet className="h-14 w-14 mx-auto text-muted-foreground/40" />
          <p className="mt-4 text-lg font-bold">لا توجد تقارير معالجة</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {isAdmin ? 'اضغط على "رفع ملف Excel" لإضافة تقرير' : 'سيتم عرض التقارير هنا فور رفعها من قِبل المدير'}
          </p>
          {isAdmin && (
            <Button onClick={handlePickFile} disabled={uploading} className="mt-6 gap-2" data-testid="upload-empty-button">
              <Upload className="h-4 w-4" />
              رفع أول تقرير
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((r) => {
            const isOpen = !!expanded[r.id];
            const totalActions = (r.employees || []).reduce((s, e) => s + (e.total || 0), 0);
            return (
              <Card key={r.id} className="border-border overflow-hidden" data-testid={`processing-report-${r.id}`}>
                <button
                  type="button"
                  onClick={() => toggle(r.id)}
                  className="w-full flex flex-col sm:flex-row sm:items-center gap-3 p-5 hover:bg-accent/50 transition-colors text-right"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <Calendar className="h-3.5 w-3.5" />
                      <span className="font-numbers">من {r.date_from || '—'} إلى {r.date_to || '—'}</span>
                    </div>
                    <p className="font-bold text-foreground truncate">{r.filename}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      رفع بواسطة {r.uploaded_by_name || '—'} · {new Date(r.uploaded_at).toLocaleDateString('ar-SA', { numberingSystem: 'latn', calendar: 'gregory' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-5">
                    <div className="text-center px-3">
                      <p className="text-xs text-muted-foreground">موظفين</p>
                      <p className="text-xl font-black text-primary font-numbers">{(r.employees || []).length}</p>
                    </div>
                    <div className="text-center px-3 border-r border-border">
                      <p className="text-xs text-muted-foreground">إجمالي الإجراءات</p>
                      <p className="text-xl font-black text-success font-numbers">{totalActions.toLocaleString('en-US')}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(ev) => { ev.stopPropagation(); handleDelete(r.id); }}
                          data-testid={`delete-processing-${r.id}`}
                          className="h-9 w-9 p-0 text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      {isOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-border bg-accent/20 p-4 space-y-3">
                    {(r.employees || []).map((emp, i) => (
                      <Card key={i} className="p-4 border-border" data-testid={`processing-employee-${r.id}-${i}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 pb-3 border-b border-border">
                          <div>
                            <p className="font-black text-foreground">{emp.name}</p>
                            <p className="text-xs text-muted-foreground font-numbers">{emp.username}</p>
                          </div>
                          <div className="text-left">
                            <p className="text-xs text-muted-foreground">الإجمالي</p>
                            <p className="text-2xl font-black text-success font-numbers">{(emp.total || 0).toLocaleString('en-US')}</p>
                          </div>
                        </div>

                        {(emp.actions || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-2">لا توجد إجراءات</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-xs text-muted-foreground">
                                  <th className="text-right py-2 px-3 font-bold">الحدث</th>
                                  <th className="text-left py-2 px-3 font-bold">عدد الإجراءات</th>
                                </tr>
                              </thead>
                              <tbody>
                                {emp.actions.map((a, j) => (
                                  <tr key={j} className="border-t border-border/50">
                                    <td className="py-2 px-3 text-foreground">{a.event}</td>
                                    <td className="py-2 px-3 text-left font-numbers font-bold text-primary">{(a.count || 0).toLocaleString('en-US')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
